<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      scrollable
      max-width="720"
      @after-leave="onDialogClosed"
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-file-document-outline</v-icon>
          <span>Generate SIPP Report</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            @click="closeD()"
          />
        </v-card-title>

        <v-card-text style="max-height: 80vh">
          <v-container>
            <!-- Report Type Selector -->
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="reportType"
                  :items="availableReportTypes"
                  item-title="label"
                  item-value="value"
                  label="Report Type"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-4"
                />
              </v-col>
            </v-row>

            <!-- Common Fields -->
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.hei"
                  label="HEI (Higher Education Institution)"
                  variant="outlined"
                  density="compact"
                  :rules="[formRules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.ay"
                  label="Academic Year (e.g. 2024-2025)"
                  variant="outlined"
                  density="compact"
                  :rules="[formRules.required]"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.address"
                  label="Address"
                  variant="outlined"
                  density="compact"
                  :rules="[formRules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.prepared_by"
                  label="Prepared By (Name)"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.certified_by"
                  :items="deans"
                  item-title="name"
                  item-value="name"
                  label="Certified Correct (Dean)"
                  variant="outlined"
                  density="compact"
                  :loading="loadingDeans"
                  clearable
                />
              </v-col>
            </v-row>

            <!-- Annex C: Degree Program + Issues Table -->
            <template v-if="reportType === 'annex-c'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="form.degree_program"
                    :items="programs"
                    item-title="name"
                    item-value="name"
                    label="Degree Program"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingPrograms"
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-2 font-weight-bold"
                  >Issues, Solutions & Recommendations</span
                >
                <!-- <v-btn
                  size="small"
                  color="primary"
                  variant="tonal"
                  prepend-icon="mdi-plus"
                  @click="addIssueRow"
                  >Add Row</v-btn
                > -->
              </div>

              <v-table density="compact" class="bordered-table mb-2">
                <thead>
                  <tr>
                    <th style="width: 33%">Issues and Concerns Encountered</th>
                    <th style="width: 33%">Solutions</th>
                    <th style="width: 28%">Recommendations</th>
                  </tr>
                </thead>
                <tbody v-if="isAnnexCLoading">
                  <tr>
                    <td colspan="3">
                      <v-skeleton-loader
                        v-for="n in 3"
                        :key="n"
                        type="list-item-two-line"
                        class="px-2"
                      />
                    </td>
                  </tr>
                </tbody>
                <tbody v-else-if="issues.length === 0">
                  <tr>
                    <td colspan="3" class="text-caption text-center pa-4">
                      No feedback found for the selected batch and degree
                      program.
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr v-for="(row, idx) in issues" :key="idx">
                    <td>
                      <div class="text-caption pa-1">{{ row.issue }}</div>
                    </td>
                    <td>
                      <div class="text-caption pa-1">{{ row.solution }}</div>
                    </td>
                    <td>
                      <div class="text-caption pa-1">
                        {{ row.recommendation }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <!-- Annex D: Batch Selector -->
            <template v-if="reportType === 'annex-d'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report will include all deployed students in the selected
                batch with their assigned HTE.
              </v-alert>
            </template>

            <!-- HTE Partner Organization Report -->
            <template v-if="reportType === 'iac-report'">
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report lists all HTE partner organizations with contact
                details, deployed intern count, and partnership status.
              </v-alert>
            </template>

            <!-- DTR Report: Batch + Hours fields -->
            <template v-if="reportType === 'dtr-report'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="form.required_hours"
                    label="Required Hours"
                    type="number"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    v-model="asOfDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="form.as_of_date"
                        label="As of Date"
                        variant="outlined"
                        density="compact"
                        readonly
                        v-bind="props"
                        prepend-inner-icon="mdi-calendar"
                        :rules="[formRules.required]"
                      />
                    </template>
                    <v-date-picker
                      v-model="asOfDateRaw"
                      @update:model-value="onAsOfDatePicked"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    v-model="targetDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="form.target_date"
                        label="Target Completion Date"
                        variant="outlined"
                        density="compact"
                        readonly
                        v-bind="props"
                        prepend-inner-icon="mdi-calendar"
                        :rules="[formRules.required]"
                      />
                    </template>
                    <v-date-picker
                      v-model="targetDateRaw"
                      @update:model-value="onTargetDatePicked"
                    />
                  </v-menu>
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report shows each student's HTE, required hours, total
                rendered hours, remaining hours, and completion status.
              </v-alert>
            </template>

            <!-- Deployment Report: Batch + Required Hours -->
            <template v-if="reportType === 'deployment-report'">
              <v-row>
                <v-col cols="12" md="8">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="form.required_hours"
                    label="Required Hours"
                    type="number"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report lists all students with their deployment details,
                HTE, internship track, hours rendered, and internship status.
              </v-alert>
            </template>

            <!-- Compliance Report: Batch -->
            <template v-if="reportType === 'compliance-report'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report shows each student's submitted and missing
                requirements, compliance status, submission date, and validator.
              </v-alert>
            </template>

            <!-- Monitoring Report: Batch -->
            <template v-if="reportType === 'monitoring-report'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report shows all monitoring visits for the selected batch —
                monitoring date, status, HTE concerns, actions taken, and
                remarks.
              </v-alert>
            </template>

            <!-- Skills Assessment and HTE Matching Report: Batch -->
            <template v-if="reportType === 'skills-matching-report'">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report shows each student's skills assessment score,
                preferred area, recommended HTE, matching basis, matching
                status, and date matched.
              </v-alert>
            </template>

            <!-- Performance Evaluation Report: Batch -->
            <template
              v-if="
                [
                  'performance-evaluation-report',
                  'student-evaluation-grading-report',
                ].includes(reportType)
              "
            >
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.batch_id"
                    :items="batches"
                    item-title="name"
                    item-value="id"
                    label="Student Batch (Academic Year)"
                    variant="outlined"
                    density="compact"
                    :rules="[formRules.required]"
                    :loading="loadingBatches"
                  />
                </v-col>
              </v-row>
              <v-alert
                type="info"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This report shows each student's HTE, evaluator, final
                evaluation score, comments, and date evaluated.
              </v-alert>
            </template>
          </v-container>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" @click="closeD()">Cancel</v-btn>
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            prepend-icon="mdi-download-outline"
            :loading="generating"
            :disabled="reportType === 'annex-c' && isAnnexCLoading"
            @click="generateReport()"
          >
            Generate PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FadeAwayMessage
      v-model="fadeAwayMessage.show"
      :type="fadeAwayMessage.type"
      :header="fadeAwayMessage.header"
      :message="fadeAwayMessage.message"
      :top="fadeAwayMessage.top"
    />
  </div>
</template>

<script>
import FadeAwayMessage from "@/components/Alerts/FadeAwayMessage.vue";

export default {
  name: "GenerateReportsDialog",
  components: { FadeAwayMessage },

  data() {
    return {
      dialog: false,
      generating: false,
      loadingBatches: false,
      reportType: "annex-c",
      reportTypes: [
        {
          label: "Annex C – Annual Report on SIPP Implementation",
          value: "annex-c",
        },
        {
          label: "Annex D – List of HTEs and Student Interns",
          value: "annex-d",
        },
        { label: "HTE Partner Organization Report", value: "iac-report" },
        { label: "Daily Time Record Summary Report", value: "dtr-report" },
        {
          label: "Student Internship Deployment Report",
          value: "deployment-report",
          coordinatorOnly: true,
        },
        {
          label: "Internship Requirements Compliance Report",
          value: "compliance-report",
          coordinatorOnly: true,
        },
        {
          label: "Student Performance Evaluation Report",
          value: "performance-evaluation-report",
          coordinatorOnly: true,
        },
        {
          label: "Skills Assessment and HTE Matching Report",
          value: "skills-matching-report",
          coordinatorOnly: true,
        },
        {
          label: "OJT Coordinator Monitoring Report",
          value: "monitoring-report",
          coordinatorOnly: true,
        },
        {
          label: "Student Evaluation Grading Report",
          value: "student-evaluation-grading-report",
          coordinatorOnly: true,
        },
      ],
      form: {
        hei: "Davao del Norte State College",
        address: "New Visayas, Panabo City, Davao del Norte, Philippines",
        ay: "",
        degree_program: "",
        prepared_by: "",
        certified_by: "",
        batch_id: null,
        iac_title: "",
        required_hours: 486,
        as_of_date: "",
        target_date: "",
      },
      asOfDateMenu: false,
      asOfDateRaw: null,
      targetDateMenu: false,
      targetDateRaw: null,
      issues: [],
      batches: [],
      programs: [],
      loadingPrograms: false,
      deans: [],
      loadingDeans: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "",
        message: "",
        top: 10,
      },
      isAnnexCLoading: false,
      // Incremented on every Annex C load so a slow, older response can't
      // overwrite the result of a newer batch / program selection.
      annexCRequestId: 0,
    };
  },

  computed: {
    availableReportTypes() {
      const usertypeID = this.$store.state.user?.user?.usertypeID;
      return this.reportTypes.filter(
        (r) => !r.coordinatorOnly || usertypeID === 3,
      );
    },
  },

  watch: {
    // Reload the Annex C table whenever the batch or degree program changes.
    // This also covers the first load: the batch and program lists arrive
    // asynchronously, and the table loads as soon as both are selected.
    "form.batch_id"() {
      this.loadAnnexC();
    },
    "form.degree_program"() {
      this.loadAnnexC();
    },
    // Switching back to Annex C refreshes the table too
    reportType(val) {
      if (val === "annex-c") this.loadAnnexC();
    },
  },

  mounted() {
    this.dialog = true;
    this.prefillUserName();
    this.loadBatches();
    this.loadPrograms();
    this.loadDeans();
  },

  methods: {
    prefillUserName() {
      const user = this.$store.state.user;
      if (user) {
        const name =
          user.name || [user.fname, user.lname].filter(Boolean).join(" ");
        this.form.prepared_by = name;
      }
    },

    async loadDeans() {
      this.loadingDeans = true;
      try {
        const res = await this.axiosCall("/user-details/getDeans", "GET");
        if (res?.data) {
          this.deans = res.data;
          if (res.data.length) this.form.certified_by = res.data[0].name;
        }
      } catch (e) {
        console.error("Failed to load deans", e);
      } finally {
        this.loadingDeans = false;
      }
    },

    async loadPrograms() {
      this.loadingPrograms = true;
      try {
        const res = await this.axiosCall(
          "/program/getProgramsByInstitute",
          "GET",
        );
        if (res?.data) {
          this.programs = res.data;
          const bsit = res.data.find((p) =>
            p.name?.toUpperCase().includes("BSIT"),
          );
          if (bsit) this.form.degree_program = bsit.name;
          else if (res.data.length) this.form.degree_program = res.data[0].name;
        }
      } catch (e) {
        console.error("Failed to load programs", e);
      } finally {
        this.loadingPrograms = false;
      }
    },

    async loadBatches() {
      this.loadingBatches = true;
      try {
        const res = await this.axiosCall(
          "/program/getProgramsBatchByInstitute",
          "GET",
        );
        if (res?.data) {
          this.batches = res.data;
          if (res.data.length) {
            this.form.batch_id = res.data[0].id;
            // pre-fill AY from batch year field
            const b = res.data[0];
            if (b.year) this.form.ay = b.year;
          }
        }
      } catch (e) {
        console.error("Failed to load batches", e);
      } finally {
        this.loadingBatches = false;
      }
    },

    formatDateDisplay(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    },

    onAsOfDatePicked(val) {
      this.form.as_of_date = this.formatDateDisplay(val);
      this.asOfDateMenu = false;
    },

    onTargetDatePicked(val) {
      this.form.target_date = this.formatDateDisplay(val);
      this.targetDateMenu = false;
    },

    // addIssueRow() {
    //   this.issues.push({ issue: "", solution: "", recommendation: "" });
    // },

    // removeIssueRow(idx) {
    //   this.issues.splice(idx, 1);
    // },

    // Loads the Issues / Solutions / Recommendations table for the selected
    // batch and degree program. Called by the watchers above.
    async loadAnnexC() {
      if (this.reportType !== "annex-c") return;

      const { batch_id, degree_program } = this.form;

      // Both are needed (the lists load one after the other on open)
      if (!batch_id || !degree_program) {
        this.issues = [];
        this.isAnnexCLoading = false;
        return;
      }

      const requestId = ++this.annexCRequestId;
      this.isAnnexCLoading = true;
      this.issues = [];

      try {
        const res = await this.axiosCall(
          "/organization-intern-feedback/getReportByStudentbatchAndProgram/" +
            batch_id +
            "/" +
            encodeURIComponent(degree_program),
          "GET",
        );

        // A newer batch / program was picked while this was loading: ignore
        if (requestId !== this.annexCRequestId) return;

        const rows = Array.isArray(res?.data) ? res.data : [];
        this.issues = rows.map((r) =>
          r.feedback_type == "general"
            ? { issue: r.feedback, solution: "", recommendation: "" }
            : {
                issue: r.feedback,
                solution: r.follow_up_note,
                recommendation: r.resolution,
              },
        );
      } catch (e) {
        console.error("Failed to load Annex C feedback", e);
        if (requestId === this.annexCRequestId) {
          this.issues = [];
          this.showMessage(
            "error",
            "Error",
            "Failed to load the issues table. Please try again.",
          );
        }
      } finally {
        if (requestId === this.annexCRequestId) {
          this.isAnnexCLoading = false;
        }
      }
    },

    async generateReport() {
      if (
        this.reportType !== "iac-report" &&
        (!this.form.hei || !this.form.address || !this.form.ay)
      ) {
        this.showMessage(
          "error",
          "Validation Error",
          "Please fill in HEI, Address, and Academic Year.",
        );
        return;
      }

      this.generating = true;
      try {
        const serverUrl = process.env.VUE_APP_SERVER;
        const token = localStorage.getItem("token");

        let url, body;
        if (this.reportType === "annex-c") {
          if (!this.form.degree_program) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please fill in the Degree Program.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/annex-c`;
          body = {
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
            degree_program: this.form.degree_program,
            prepared_by: this.form.prepared_by,
            certified_by: this.form.certified_by,
            issues: this.issues.filter(
              (r) => r.issue || r.solution || r.recommendation,
            ),
          };
        } else if (this.reportType === "annex-d") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/annex-d`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
            prepared_by: this.form.prepared_by,
            certified_by: this.form.certified_by,
          };
        } else if (this.reportType === "iac-report") {
          url = `${serverUrl}/pdfs/iac-report`;
          body = {
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
            title: "HTE Summary Report",
          };
        } else if (this.reportType === "dtr-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          if (!this.form.as_of_date || !this.form.target_date) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please fill in the As of Date and Target Completion Date.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/dtr-report`;
          body = {
            batch_id: this.form.batch_id,
            required_hours: this.form.required_hours || 486,
            as_of_date: this.form.as_of_date,
            target_date: this.form.target_date,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else if (this.reportType === "deployment-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/deployment-report`;
          body = {
            batch_id: this.form.batch_id,
            required_hours: this.form.required_hours || 486,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else if (this.reportType === "compliance-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/compliance-report`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else if (this.reportType === "monitoring-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/monitoring-report`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else if (this.reportType === "performance-evaluation-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/performance-evaluation-report`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else if (this.reportType === "student-evaluation-grading-report") {
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/student-evaluation-grading-report`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        } else {
          // skills-matching-report
          if (!this.form.batch_id) {
            this.showMessage(
              "error",
              "Validation Error",
              "Please select a student batch.",
            );
            this.generating = false;
            return;
          }
          url = `${serverUrl}/pdfs/skills-matching-report`;
          body = {
            batch_id: this.form.batch_id,
            hei: this.form.hei,
            address: this.form.address,
            ay: this.form.ay,
          };
        }

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error("PDF generation failed");

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl);
      } catch (e) {
        console.error("Generate report error", e);
        this.showMessage(
          "error",
          "Error",
          "Failed to generate report. Please try again.",
        );
      } finally {
        this.generating = false;
      }
    },

    showMessage(type, header, message) {
      this.fadeAwayMessage = { show: true, type, header, message, top: 10 };
    },

    closeD() {
      this.dialog = false;
    },

    onDialogClosed() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.bordered-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.bordered-table th {
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  padding: 8px 10px;
}
.bordered-table td {
  border-top: 1px solid #e0e0e0;
  padding: 4px 6px;
  vertical-align: top;
}
</style>
